import { TitleCasePipe } from '@angular/common';
import { Component, computed, inject, input, model, output, resource, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { OauthService } from '../../../services/oauth.service';
import { LocalDbService } from '../../../services/local-db.service';
import { AbilityServiceSignal } from '@casl/angular';
import { AppAbility } from '../../../services/permissions.service';
import { RoleLike, User } from '../../../data/schemas/user.schema';
import { MenuItem } from '../../../data/models/menu-item.model';
import { logos } from '../properties';
import { GlobalTexts } from '../globalText';
import packageInfo from '../../../../../package.json';
import { ChangelogDlgComponent, SystemInfoDialogData } from '../../home/changelog-dlg/changelog-dlg.component';
import { MENU_SUBJECTS } from './menu-subjects';
import { filterMenu, getRoleDescription, isUrlActive } from './side-nav-menu';

@Component({
  selector: 'app-side-nav',
  imports: [TitleCasePipe, MatButtonModule, MatTooltipModule, MatIconModule, RouterLink, RouterLinkActive],
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
})
export class SideNavComponent {
  readonly opened = model(true);
  readonly showNav = input(true);
  readonly user = input<User | null>(null);
  readonly userRoleName = input('');

  readonly logout = output<void>();
  readonly menuClick = output<MenuItem>();

  private readonly localDbService = inject(LocalDbService);
  protected readonly router = inject(Router);
  protected readonly oauthSvc = inject(OauthService);
  protected readonly gTexts = inject(GlobalTexts);
  private readonly dialog = inject(MatDialog);
  private readonly abilityService = inject<AbilityServiceSignal<AppAbility>>(AbilityServiceSignal);
  protected readonly can = this.abilityService.can;

  private readonly baseMenu: MenuItem[] = [
    {
      label: 'Inicio',
      icon: 'home',
      url: 'home',
    },
    {
      label: 'Trazabilidad',
      icon: 'list',
      children: [
        {
          label: 'Asignaciones',
          icon: 'swap_horiz',
          url: 'asignaciones',
          subject: MENU_SUBJECTS.assignments,
        },
        {
          label: 'Traslado a curar',
          icon: 'move_down',
          url: 'trazabilidad/traslado-curado',
          subject: MENU_SUBJECTS.curingTransfer,
        },
        {
          label: 'Curado',
          icon: 'science',
          url: 'trazabilidad/curado',
          subject: MENU_SUBJECTS.curing,
        },
        {
          label: 'Salida al campo',
          icon: 'agriculture',
          url: 'trazabilidad/salida-campo',
          subject: MENU_SUBJECTS.seedBackoffice,
        },
        {
          label: 'Fletes',
          icon: 'local_shipping',
          url: 'trazabilidad/fletes',
          subject: MENU_SUBJECTS.shipments,
        },
      ]
    },
    {
      label: 'Trazabilidad UNAGRO',
      icon: 'inventory_2',
      url: 'unagro/lotes',
      subject: MENU_SUBJECTS.seedUnagro,
      action: 'view_stock',
    },
    /*     {
      label: 'Facturas',
      icon: 'request_quote',
      url: 'facturas',
      subject: 'invoices',
    },
  {
    label: 'Semilla a Procesar',
    icon: 'psychiatry',
    url: 'lotes',
    subject: 'seed-management'
  },
  {
    label: 'Despacho de lotes',
    icon: 'local_shipping',
    url: 'despacho',
    subject: 'shipments'
  }, */
    {
      label: 'Edición de Kg',
      icon: 'scale',
      url: 'edita-kg',
      subject: MENU_SUBJECTS.seedManagement,
    },
    {
      label: 'Usuarios',
      icon: 'person',
      url: 'users',
      subject: MENU_SUBJECTS.users,
    },
    {
      label: 'Roles',
      icon: 'groups_3',
      url: 'roles',
      subject: MENU_SUBJECTS.roles,
    },
  ];

  protected readonly currentYear = new Date().getFullYear();
  protected readonly version: string = packageInfo.version;
  protected readonly appLogoSrc: string = logos.app;
  protected readonly companyLogoSrc: string = logos.agd;

  /** Labels de los grupos expandidos. Varios grupos pueden estar abiertos a la vez. */
  readonly expanded = signal<ReadonlySet<string>>(new Set());

  readonly userResource = resource({
    loader: async () => {
      const savedData = await this.localDbService.getLoginData();
      if (savedData) {
        return savedData;
      }

      return {
        token: 'mock-jwt-token',
        user: {
          id: 0,
          firstName: 'Usuario',
          lastName: 'de Prueba',
          email: 'usuario.social@gmail.com',
          dni: '9122018',
          roles: [{ id: 1, name: 'USUARIO', description: 'Acceso Estándar (Social)' }],
        },
      };
    },
  });

  readonly userRolesDescriptions = computed(() => {
    const roles: RoleLike[] = this.userResource.value()?.user?.roles || [];
    return roles.map(getRoleDescription);
  });

  readonly menu = computed(() =>
    filterMenu(this.baseMenu, (item) => !item.subject || this.can(item.action ?? 'read', item.subject)),
  );

  constructor() {
    this.expandActiveGroups(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.userResource.reload();
        this.expandActiveGroups(event.urlAfterRedirects);
      });
  }

  isExpanded(label: string): boolean {
    return this.expanded().has(label);
  }

  toggleGroup(label: string) {
    this.expanded.update((current) => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }

  onMenuClick(item: MenuItem) {
    if (item.url) {
      this.goToPage(item.url);
    }
  }

  onLogout() {
    this.oauthSvc.logout().subscribe({ error: () => undefined });
    this.router.navigate(['/login']);
  }

  toggleSidenav() {
    this.opened.update((opened) => !opened);
  }

  goToPage(uri: string) {
    this.router.navigate(['/' + uri]);
  }

  viewChangeLog() {
    this.dialog.open(ChangelogDlgComponent, {
      width: '600px',
      maxHeight: '80vh',
      data: { initialTab: 'servicios' } satisfies SystemInfoDialogData,
    });
  }

  /** Expande (sin colapsar los demás) los grupos que contienen la ruta activa. */
  private expandActiveGroups(currentUrl: string) {
    const active = this.baseMenu
      .filter((item) => item.children?.some((child) => child.url && isUrlActive(child.url, currentUrl)))
      .map((item) => item.label);

    if (active.every((label) => this.expanded().has(label))) return;
    this.expanded.update((current) => new Set([...current, ...active]));
  }
}
